import * as React from 'react';
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {logIn} from "../../store/action/user/user";
import {none, Option, option} from "ts-option";
import {OnFailure, OnSuccess} from "../../store/action/actionHelpers";
import * as validator from "validator";
import Routes from "../../navigation/route/Route";
import "./logInPage.scss";
import ButtonPrimary from "../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import Input from "../../hiberUiKit/src/atoms/Input/Input";
import DrivingGreenLogo from "./DrivingGreenLogo";


interface DispatchProps {
	/**
	 * Login dispatch action.
	 * @param {string} username User's username.
	 * @param {string} password User's password
	 */
	logIn: (username: string, password: string, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface State {
	/**
	 * Username.
	 */
	username: Option<string>,
	/**
	 * Password
	 */
	password: Option<string>,
	/**
	 * Indicates if logging in is in progress.
	 */
	loading: boolean,
	/**
	 * Error from login attempt.
	 */
	error: Option<string>
	/**
	 * Disabled user error
	 */
	disabledUserError: boolean
	/**
	 * Field with focus.
	 */
	focus: "username" | "password"
}

class LogInPage extends React.Component<DispatchProps, State> {

	constructor(props: DispatchProps) {
		super(props);
		this.state = {username: none, password: none, loading: false, error: none, focus: "username", disabledUserError: false};
		this.updatePassword = this.updatePassword.bind(this);
		this.updateUsername = this.updateUsername.bind(this);
	}

	render() {
		return (
			<div className={"c-login-page"}>
				<div className="c-login-page_content">
					<DrivingGreenLogo className="c-login-page_content_logo"/>
					<h1 className="c-login-page_content_header">Log into your DG account</h1>
					<p className="c-login-page_content_intro">Enter your username and password to get started.</p>
					<div className="c-login-page_content_form">
						<div className="c-login-page_content_form_user">
							<label htmlFor="login-username">Username</label>
							<Input id="login-username"
								   type={"text"}
								   value={this.state.username.getOrElse("")}
								   onChange={this.updateUsername}
								   placeholder={"Enter your username"}
								   focus={this.state.focus == "username"}
								   onConfirm={() => this.setState({focus: "password"})}/>
						</div>
						<div className="c-login-page_content_form_password">
							<label htmlFor="login-password">Password</label>
							<Input id="login-password"
								   type={"password"}
								   value={this.state.password.getOrElse("")}
								   onChange={this.updatePassword}
								   placeholder={"Enter your password"}
								   focus={this.state.focus == "password"}
								   onConfirm={() => this.onSignInClick()}/>
						</div>
						<div className="c-login-page_content_form_forgot">
							<a href={Routes.ForgotPassword}>Forgotten your password?</a>
						</div>
						<div className={"c-login-page_content_form_error"} role="alert" aria-live="polite">
							{this.renderError()}
							{this.renderDisabledUserError()}
						</div>
						<div className={"c-login-page_content_form_button"}>
							<ButtonPrimary label={"Login"} variant={"success"} onClick={() => this.onSignInClick()} isLoading={this.state.loading}/>
						</div>
					</div>
				</div>
			</div>
		)
	}

	/**
	 * Render Sign in error.
	 */
	renderError() {
		return this.state.disabledUserError &&
			<span>Your account has been disabled. <br/> Please contact your organization administrator</span>
	}

	/**
	 * Render Sign in error.
	 */
	renderDisabledUserError() {
		return this.state.error.map(error => <span>{error}</span>).orNull
	}

	/**
	 * Update username.
	 */
	updateUsername(evt: React.ChangeEvent<HTMLInputElement>) {
		this.setState({username: option(evt.target.value), error: none, disabledUserError: false});
	};

	/**
	 * Update password.
	 */
	updatePassword(evt: React.ChangeEvent<HTMLInputElement>) {
		this.setState({password: option(evt.target.value), error: none, disabledUserError: false});
	};

	/**
	 * On sign in click.
	 */
	private onSignInClick() {
		const error = this.validateInputs();
		this.setState({loading: error.isEmpty, error, disabledUserError: false});
		if (error.isEmpty)
			this.props.logIn(this.state.username.get, this.state.password.get, this.onSuccess.bind(this),
				this.onFailure.bind(this));
	}

	/**
	 * Validate inputs.
	 * @returns {Option<string>} Optional error containing error string to display.
	 */
	private validateInputs(): Option<string> {
		const username = this.state.username.getOrElse("");
		const password = this.state.password.getOrElse("");
		if (validator.isEmpty(username) && validator.isEmpty(password))
			return option("Please fill password and username");
		else if (validator.isEmpty(username))
			return option("Please fill username");
		else if (validator.isEmpty(password))
			return option("Please fill password");
		else if (password.length < 5)
			return option("Password is too short");
		else
			return none;
	}

	/**
	 * On success signing in.
	 */
	private onSuccess() {
		this.setState({loading: false, error: none, disabledUserError: false});
	}

	/**
	 * On failure signing in.
	 * @param error Error message.
	 */
	private onFailure(error: any) {
		let err: Option<string> = none;
		if (error && error.response)
			switch (error.response.status) {
				case 401:
					err = option("Username or password is incorrect");
					break;
				case 403:
					//User disabled
					if (error.response.data.extraStatus == "DISABLED") {
						this.setState({disabledUserError: true});
						break;
					}
				default:
					err = option("Error while logging in. Please try again later");
			}
		else {
			err = option("Error while logging in. Please try again later");
		}
		this.setState({loading: false, error: err});
	}

}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		logIn: (username: string, password: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			logIn(username, password, onSuccess, onFailure))
	}
}

export default connect(null, mapDispatchToProps)(LogInPage);
